import { create } from 'zustand';
import { CURRENT_USER } from '../data/mockData';
import { api, readAuthToken, WS_URL } from '../api/client';
import { contactsById, filterCustomerContacts } from './contactState';

let ws;
let reconnectTimer;
const USER_SETTINGS_KEY = 'seeme:user-settings';

const readUserSettings = () => {
  try {
    const raw = window.localStorage.getItem(USER_SETTINGS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    return {};
  }
};

const writeUserSettings = (settings) => {
  try {
    window.localStorage.setItem(USER_SETTINGS_KEY, JSON.stringify(settings));
  } catch (error) {
    // Ignore storage failures so the app remains usable in restricted browsers.
  }
};

const normalizeConversations = (items) => {
  const contacts = {};
  const conversations = items.map(conv => {
    contacts[conv.contactId] = conv.contact;
    return { ...conv, messages: [] };
  });
  return { conversations, contacts };
};

const mergeMessage = (messages, message) => {
  const index = messages.findIndex(item => item.id === message.id);
  if (index === -1) {
    return [...messages, message];
  }

  return messages.map((item, itemIndex) => itemIndex === index ? message : item);
};

const isMissingConversationError = (error) =>
  error?.status === 404 && error?.path?.includes('/conversations/');

const savedUserSettings = readUserSettings();

export const useStore = create((set, get) => ({
  // State
  conversations: [],
  contacts: {},
  customerContacts: [],
  activeConvId: undefined,
  loading: false,
  error: null,
  wsStatus: 'idle',
  conversationStatus: 'open',
  filterPlatform: 'all',
  searchQuery: '',
  contactSearchQuery: '',
  tasks: [],
  tasksLoading: false,
  contactsLoading: false,
  analytics: null,
  analyticsLoading: false,
  currentUser: { ...CURRENT_USER, ...(savedUserSettings.currentUser || {}) },
  showCrm: savedUserSettings.showCrm ?? true,
  reminderComposerOpen: false,
  translatingMsgId: null,
  wecomStates: {},
  wecomStateLoading: false,

  // Computed
  getActiveConv: () => {
    const { conversations, activeConvId } = get();
    return conversations.find(c => c.id === activeConvId);
  },
  getActiveContact: () => {
    const { contacts, getActiveConv } = get();
    const conv = getActiveConv();
    return conv ? contacts[conv.contactId] : null;
  },
  getFilteredConvs: () => {
    const { conversations, contacts, filterPlatform, searchQuery } = get();
    return conversations.filter(conv => {
      const contact = contacts[conv.contactId];
      if (!contact) return false;
      const matchPlatform = filterPlatform === 'all' || conv.platform === filterPlatform;
      const matchSearch = !searchQuery ||
        (contact.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (contact.company || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (conv.lastMessage || '').toLowerCase().includes(searchQuery.toLowerCase());
      return matchPlatform && matchSearch;
    });
  },
  getContactList: () => {
    const { customerContacts, contactSearchQuery } = get();
    return filterCustomerContacts(customerContacts, contactSearchQuery);
  },

  // Actions
  connectWebSocket: () => {
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    const token = readAuthToken();
    if (!token) {
      set({ wsStatus: 'idle' });
      return;
    }
    window.clearTimeout(reconnectTimer);
    set({ wsStatus: 'connecting' });
    ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}`);

    ws.onopen = () => {
      set({ wsStatus: 'connected' });
      ws.send(JSON.stringify({ event: 'ping' }));
    };
    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.event === 'message.new') {
          get().applyIncomingMessage(payload.convId, payload.message, payload.conversation);
        }
        if (payload.event === 'message.updated') {
          get().applyUpdatedMessage(payload.convId, payload.message);
        }
        if (payload.event === 'message.status') {
          get().applyMessageStatus(payload.msgId, payload.status);
        }
      } catch (error) {
        set({ error: error.message });
      }
    };
    ws.onclose = () => {
      set({ wsStatus: 'disconnected' });
      reconnectTimer = window.setTimeout(() => get().connectWebSocket(), 2500);
    };
    ws.onerror = () => {
      set({ wsStatus: 'error' });
      ws.close();
    };
  },

  disconnectWebSocket: () => {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = null;
    if (ws) {
      const current = ws;
      ws = null;
      current.onclose = null;
      current.close();
    }
    set({ wsStatus: 'idle' });
  },

  applyIncomingMessage: (convId, message, conversation) => set(state => {
    const existing = state.conversations.find(conv => conv.id === convId);
    const contacts = conversation?.contact
      ? { ...state.contacts, [conversation.contactId]: conversation.contact }
      : state.contacts;

    if (!existing && conversation) {
      return {
        contacts,
        conversations: [
          {
            ...conversation,
            messages: [message],
            lastMessage: message.text || conversation.lastMessage,
            lastTime: message.time || conversation.lastTime,
            unread: state.activeConvId === convId ? 0 : Math.max(conversation.unread || 0, 1)
          },
          ...state.conversations
        ]
      };
    }

    let updated;
    const conversations = state.conversations.map(conv => {
      if (conv.id !== convId) return conv;
      const exists = conv.messages.some(item => item.id === message.id);
      updated = {
        ...conv,
        messages: mergeMessage(conv.messages, message),
        lastMessage: message.text || conv.lastMessage,
        lastTime: message.time || conv.lastTime,
        unread: state.activeConvId === convId || exists ? conv.unread : conv.unread + 1
      };
      return updated;
    });

    if (!updated) {
      return { contacts };
    }

    return {
      contacts,
      conversations: [
        updated,
        ...conversations.filter(conv => conv.id !== convId)
      ]
    };
  }),

  applyUpdatedMessage: (convId, message) => set(state => ({
    conversations: state.conversations.map(conv => {
      if (conv.id !== convId) return conv;
      return {
        ...conv,
        messages: conv.messages.map(item => item.id === message.id ? message : item)
      };
    })
  })),

  applyMessageStatus: (messageId, status) => set(state => ({
    conversations: state.conversations.map(conv => ({
      ...conv,
      messages: conv.messages.map(item =>
        item.id === messageId ? { ...item, status } : item
      )
    }))
  })),

  loadConversations: async () => {
    set({ loading: true, error: null });
    try {
      const items = await api.listConversations(get().conversationStatus);
      const normalized = normalizeConversations(items);
      const activeConvId = normalized.conversations.some(c => c.id === get().activeConvId)
        ? get().activeConvId
        : normalized.conversations[0]?.id;
      set({ ...normalized, activeConvId, loading: false });
      if (activeConvId) {
        await get().loadMessages(activeConvId);
      }
    } catch (error) {
      set({ loading: false, error: error.message });
    }
  },

  loadMessages: async (convId) => {
    try {
      const messages = await api.listMessages(convId);
      set(state => ({
        conversations: state.conversations.map(c =>
          c.id === convId ? { ...c, messages } : c
        )
      }));
    } catch (error) {
      if (isMissingConversationError(error)) {
        await get().loadConversations();
        return;
      }
      set({ error: error.message });
    }
  },

  loadWeComState: async (convId) => {
    if (!convId) return null;
    const conv = get().conversations.find(item => item.id === convId);
    if (!conv || conv.platform !== 'wecom') return null;
    set({ wecomStateLoading: true });
    try {
      const state = await api.getWeComConversationState(convId);
      set(current => ({
        error: null,
        wecomStateLoading: false,
        wecomStates: {
          ...current.wecomStates,
          [convId]: state
        }
      }));
      return state;
    } catch (error) {
      set({ wecomStateLoading: false, error: error.message });
      return null;
    }
  },

  setActiveConv: async (convId) => {
    set(state => ({
      activeConvId: convId,
      conversations: state.conversations.map(c =>
        c.id === convId ? { ...c, unread: 0 } : c
      )
    }));
    try {
      await Promise.all([api.markRead(convId), get().loadMessages(convId)]);
      await get().loadWeComState(convId);
    } catch (error) {
      if (isMissingConversationError(error)) {
        await get().loadConversations();
        return;
      }
      set({ error: error.message });
    }
  },

  closeConversation: async (convId) => {
    try {
      await api.closeConversation(convId);
      set(state => {
        const conversations = state.conversations.filter(conv => conv.id !== convId);
        const activeConvId = state.activeConvId === convId ? conversations[0]?.id : state.activeConvId;
        return { error: null, conversations, activeConvId };
      });
      const next = get().activeConvId;
      if (next) {
        await get().loadMessages(next);
      }
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  reopenConversation: async (convId) => {
    try {
      const conv = await api.reopenConversation(convId);
      set(state => ({
        error: null,
        conversationStatus: 'open',
        activeConvId: conv.id,
        contacts: {
          ...state.contacts,
          [conv.contactId]: conv.contact
        },
        conversations: [
          { ...conv, messages: [] },
          ...state.conversations.filter(item => item.id !== conv.id)
        ]
      }));
      await get().loadMessages(conv.id);
      return conv;
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  setFilterPlatform: (platform) => set({ filterPlatform: platform }),
  setSearchQuery: (q) => set({ searchQuery: q }),
  setContactSearchQuery: (q) => set({ contactSearchQuery: q }),
  setConversationStatus: async (status) => {
    set({ conversationStatus: status, activeConvId: undefined });
    await get().loadConversations();
  },
  clearError: () => set({ error: null }),
  toggleCrm: () => set(state => {
    const showCrm = !state.showCrm;
    writeUserSettings({ currentUser: state.currentUser, showCrm });
    return { showCrm };
  }),
  updateUserSettings: (updates) => set(state => {
    const currentUser = {
      ...state.currentUser,
      ...updates,
      initials: (updates.initials || state.currentUser.initials || '?').slice(0, 2),
    };
    writeUserSettings({ currentUser, showCrm: state.showCrm });
    return { currentUser };
  }),
  setCrmPreference: (showCrm) => set(state => {
    writeUserSettings({ currentUser: state.currentUser, showCrm });
    return { showCrm };
  }),
  openReminderComposer: () => set({ showCrm: true, reminderComposerOpen: true }),
  closeReminderComposer: () => set({ reminderComposerOpen: false }),

  loadContacts: async () => {
    set({ contactsLoading: true });
    try {
      const items = await api.listContacts();
      set({
        contactsLoading: false,
        error: null,
        customerContacts: items,
        contacts: {
          ...get().contacts,
          ...contactsById(items)
        }
      });
    } catch (error) {
      set({ contactsLoading: false, error: error.message });
    }
  },

  openContact: async (contactId) => {
    const conv = get().conversations.find(item => item.contactId === contactId);
    if (conv) {
      await get().setActiveConv(conv.id);
      return;
    }
  },

  startConversation: async (contactId, platform = 'email') => {
    try {
      const conv = await api.createConversation({ contactId, platform });
      set(state => ({
        error: null,
        conversationStatus: 'open',
        activeConvId: conv.id,
        contacts: {
          ...state.contacts,
          [conv.contactId]: conv.contact
        },
        conversations: [
          { ...conv, messages: [] },
          ...state.conversations.filter(item => item.id !== conv.id)
        ]
      }));
      await get().loadMessages(conv.id);
      return conv;
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  createContact: async (payload) => {
    try {
      const contact = await api.createContact(payload);
      set(state => ({
        error: null,
        contacts: {
          [contact.id]: contact,
          ...state.contacts
        },
        customerContacts: [contact, ...state.customerContacts.filter(item => item.id !== contact.id)]
      }));
      return contact;
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  deleteContact: async (contactId) => {
    try {
      await api.deleteContact(contactId);
      set(state => {
        const { [contactId]: _removed, ...contacts } = state.contacts;
        return {
          error: null,
          contacts,
          customerContacts: state.customerContacts.filter(contact => contact.id !== contactId),
          tasks: state.tasks.filter(task => task.contactId !== contactId)
        };
      });
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  loadAnalytics: async () => {
    set({ analyticsLoading: true });
    try {
      const analytics = await api.getAnalyticsSummary();
      set({ analytics, analyticsLoading: false, error: null });
    } catch (error) {
      set({ analyticsLoading: false, error: error.message });
    }
  },

  loadTasks: async () => {
    set({ tasksLoading: true });
    try {
      const tasks = await api.listTasks();
      set({ tasks, tasksLoading: false, error: null });
    } catch (error) {
      set({ tasksLoading: false, error: error.message });
    }
  },

  openTaskContact: async (task) => {
    if (!task?.conversationId) {
      return;
    }
    await get().setActiveConv(task.conversationId);
  },

  completeTask: async (taskId) => {
    try {
      await api.completeTask(taskId);
      set(state => ({
        error: null,
        tasks: state.tasks.filter(task => task.id !== taskId),
        contacts: Object.fromEntries(Object.entries(state.contacts).map(([contactId, contact]) => [
          contactId,
          {
            ...contact,
            reminders: (contact.reminders || []).filter(reminder => reminder.id !== taskId)
          }
        ]))
      }));
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  translateText: async (text, targetLang = 'en') => {
    try {
      const result = await api.translate(text, targetLang);
      set({ error: null });
      return result.text;
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  translateMessage: async (messageId, targetLang = 'zh') => {
    const conv = get().getActiveConv();
    if (!conv) return null;
    set({ translatingMsgId: messageId });
    try {
      const updated = await api.translateMessage(conv.id, messageId, targetLang);
      set(state => ({
        error: null,
        translatingMsgId: null,
        conversations: state.conversations.map(c =>
          c.id === conv.id
            ? { ...c, messages: c.messages.map(item => item.id === messageId ? updated : item) }
            : c
        )
      }));
      return updated;
    } catch (error) {
      set({ error: error.message, translatingMsgId: null });
      throw error;
    }
  },

  uploadFile: async (file) => {
    try {
      const uploaded = await api.uploadFile(file);
      set({ error: null });
      return uploaded;
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  sendMessage: async (message) => {
    const conv = get().getActiveConv();
    if (!conv) return {};
    try {
      const payload = typeof message === 'string' ? { text: message } : message;
      const newMsg = await api.sendMessage(conv.id, payload);
      set(state => ({
        error: null,
        conversations: state.conversations.map(c =>
          c.id === conv.id
            ? { ...c, messages: mergeMessage(c.messages, newMsg), lastMessage: newMsg.text, lastTime: newMsg.time }
            : c
        )
      }));
      if (conv.platform === 'wecom') {
        get().loadWeComState(conv.id);
      }
      return newMsg;
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  sendQuote: async (quote = {}) => {
    const conv = get().getActiveConv();
    if (!conv) return {};
    try {
      const newMsg = await api.sendQuote(conv.id, quote);
      set(state => ({
        error: null,
        conversations: state.conversations.map(c =>
          c.id === conv.id
            ? { ...c, messages: mergeMessage(c.messages, newMsg), lastMessage: newMsg.text, lastTime: newMsg.time }
            : c
        )
      }));
      return newMsg;
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  updateContact: async (contactId, updates) => {
    try {
      const updated = await api.updateContact(contactId, updates);
      set(state => ({
        error: null,
        contacts: {
          ...state.contacts,
          [contactId]: updated
        },
        customerContacts: state.customerContacts.map(contact => contact.id === contactId ? updated : contact),
        conversations: state.conversations.map(c =>
          c.contactId === contactId ? { ...c, contact: updated } : c
        )
      }));
      return updated;
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  addReminder: async (contactId, reminder) => {
    try {
      const saved = await api.addReminder(contactId, reminder);
      set(state => ({
        error: null,
        tasks: [
          {
            ...saved,
            contactId,
            contactName: state.contacts[contactId]?.name,
            company: state.contacts[contactId]?.company,
            conversationId: state.conversations.find(conv => conv.contactId === contactId)?.id,
            platform: state.conversations.find(conv => conv.contactId === contactId)?.platform,
          },
          ...state.tasks
        ],
        contacts: {
          ...state.contacts,
          [contactId]: {
            ...state.contacts[contactId],
            reminders: [...(state.contacts[contactId].reminders || []), saved]
          }
        }
      }));
      return saved;
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  removeReminder: async (contactId, reminderId) => {
    try {
      await api.removeReminder(contactId, reminderId);
      set(state => ({
        error: null,
        tasks: state.tasks.filter(task => task.id !== reminderId),
        contacts: {
          ...state.contacts,
          [contactId]: {
            ...state.contacts[contactId],
            reminders: (state.contacts[contactId].reminders || []).filter(r => r.id !== reminderId)
          }
        }
      }));
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  addOrder: async (contactId, order) => {
    try {
      const saved = await api.addOrder(contactId, order);
      set(state => ({
        error: null,
        contacts: {
          ...state.contacts,
          [contactId]: {
            ...state.contacts[contactId],
            orders: [saved, ...state.contacts[contactId].orders]
          }
        }
      }));
      return saved;
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  removeOrder: async (contactId, orderId) => {
    try {
      await api.removeOrder(contactId, orderId);
      set(state => ({
        error: null,
        contacts: {
          ...state.contacts,
          [contactId]: {
            ...state.contacts[contactId],
            orders: state.contacts[contactId].orders.filter(order => order.id !== orderId)
          }
        }
      }));
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },
}));
